
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { ApprovalRequest, UserWithRole } from "./types";

// Define interface for RPC function responses
interface AdminRpcResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const fetchUsers = async (): Promise<UserWithRole[]> => {
  try {
    console.log('Fetching users with admin function...');
    
    // Use admin-safe function to get all users
    const { data: users, error } = await supabase.rpc('admin_get_all_users');
    
    if (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users: ' + error.message);
      return [];
    }
    
    console.log('Users fetched successfully:', users?.length || 0);
    
    // Transform the data to match UserWithRole interface
    const usersWithRoles: UserWithRole[] = (users || []).map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      is_approved: user.is_approved,
      created_at: user.created_at,
      avatar_url: user.avatar_url,
      role: user.role as UserWithRole['role']
    }));
    
    return usersWithRoles;
  } catch (error) {
    console.error('Error fetching users:', error);
    toast.error('Failed to load users');
    return [];
  }
};

export const fetchApprovalRequests = async (): Promise<ApprovalRequest[]> => {
  try {
    console.log('Fetching approval requests...');
    
    const { data: requests, error } = await supabase
      .from('user_approval_requests')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching approval requests:', error);
      throw error;
    }

    console.log('Approval requests fetched:', requests?.length || 0);

    // Get user details for each request
    const requestsWithUserDetails = await Promise.all((requests || []).map(async (request) => {
      console.log('Fetching profile for user:', request.user_id);
      
      const { data: userData, error: profileError } = await supabase
        .from('profiles')
        .select('email, name')
        .eq('id', request.user_id)
        .single();

      if (profileError) {
        console.warn('Error fetching profile for user:', request.user_id, profileError);
      }

      return {
        ...request,
        user_email: userData?.email || 'No email available',
        user_name: userData?.name || 'Unknown User',
        status: request.status as ApprovalRequest['status']
      };
    }));

    console.log('Approval requests with user details:', requestsWithUserDetails);
    return requestsWithUserDetails as ApprovalRequest[];
  } catch (error) {
    console.error('Error fetching approval requests:', error);
    toast.error('Failed to load approval requests');
    return [];
  }
};

export const updateUserRole = async (userId: string, role: UserWithRole['role']): Promise<boolean> => {
  try {
    console.log('Updating user role:', { userId, role });
    
    // Use admin-safe function to update user role
    const { data, error } = await supabase.rpc('admin_update_user_role', {
      target_user_id: userId,
      new_role: role
    });
    
    if (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role: ' + error.message);
      return false;
    }

    // Check if the operation was successful
    const result = data?.[0];
    if (result?.success) {
      console.log('User role updated successfully');
      toast.success('User role updated successfully');
      return true;
    } else {
      console.error('Role update failed:', result?.message);
      toast.error(result?.message || 'Failed to update user role');
      return false;
    }
  } catch (error) {
    console.error('Error updating user role:', error);
    toast.error('Failed to update user role');
    return false;
  }
};

// NEW: Use the safe admin function for approval
export const approveUser = async (userId: string, reviewerId: string): Promise<boolean> => {
  try {
    console.log('Approving user with safe function:', { userId, reviewerId });
    
    // Use the new safe admin function
    const { data, error } = await supabase.rpc('admin_approve_user_safe', {
      target_user_id: userId,
      reviewer_id: reviewerId
    });
    
    if (error) {
      console.error('Error in admin_approve_user_safe:', error);
      toast.error('Failed to approve user: ' + error.message);
      return false;
    }

    console.log('Safe approval function result:', data);

    // Cast the response to the expected type via unknown
    const response = data as unknown as AdminRpcResponse;

    // Check the result from the function
    if (response?.success) {
      console.log('User approved successfully');
      toast.success(response.message || "User has been approved successfully");
      return true;
    } else {
      console.error('Approval failed:', response?.error);
      toast.error(response?.error || 'Failed to approve user');
      return false;
    }
  } catch (error) {
    console.error('Error approving user:', error);
    toast.error('Failed to approve user: ' + (error as any)?.message);
    return false;
  }
};

// NEW: Use the safe admin function for rejection
export const rejectUser = async (userId: string, reviewerId: string): Promise<boolean> => {
  try {
    console.log('Rejecting user with safe function:', { userId, reviewerId });
    
    // Use the new safe admin function
    const { data, error } = await supabase.rpc('admin_reject_user_safe', {
      target_user_id: userId,
      reviewer_id: reviewerId
    });
    
    if (error) {
      console.error('Error in admin_reject_user_safe:', error);
      toast.error('Failed to reject user: ' + error.message);
      return false;
    }

    console.log('Safe rejection function result:', data);

    // Cast the response to the expected type via unknown
    const response = data as unknown as AdminRpcResponse;

    // Check the result from the function
    if (response?.success) {
      console.log('User request rejected successfully');
      toast.success(response.message || "User request has been rejected");
      return true;
    } else {
      console.error('Rejection failed:', response?.error);
      toast.error(response?.error || 'Failed to reject user');
      return false;
    }
  } catch (error) {
    console.error('Error rejecting user:', error);
    toast.error('Failed to reject user: ' + (error as any)?.message);
    return false;
  }
};

export const toggleApproval = async (userId: string, isApproved: boolean): Promise<boolean> => {
  try {
    console.log('Toggling user approval:', { userId, isApproved });
    
    const { error } = await supabase
      .from('profiles')
      .update({ is_approved: isApproved })
      .eq('id', userId);
    
    if (error) {
      console.error('Error toggling approval:', error);
      throw error;
    }

    console.log('User approval toggled successfully');
    toast.success(`User ${isApproved ? 'approved' : 'unapproved'} successfully`);
    return true;
  } catch (error) {
    console.error('Error toggling approval:', error);
    toast.error('Failed to update user approval status: ' + (error as any)?.message);
    return false;
  }
};

export const deleteUser = async (userId: string): Promise<boolean> => {
  try {
    console.log('Deleting user via edge function:', userId);
    
    // Get the current session to ensure we have a valid token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.error('No active session');
      toast.error('You must be logged in to delete users');
      return false;
    }
    
    console.log('Calling edge function with user ID:', userId);
    
    const { data, error } = await supabase.functions.invoke('admin-delete-user', {
      body: { userId }
    });
    
    console.log('Edge function response:', { data, error });
    
    if (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user: ' + error.message);
      return false;
    }
    
    const response = data as { success?: boolean; message?: string; error?: string; blockers?: any };
    
    if (response?.success) {
      toast.success(response.message || 'User deleted successfully');
      return true;
    } else {
      if (response?.blockers?.certificates) {
        toast.error('Cannot delete user: User has issued certificates');
      } else {
        toast.error(response?.error || 'Failed to delete user');
      }
      return false;
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    toast.error('Failed to delete user');
    return false;
  }
};
